import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { ApolloClient, ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { CONFIG } from '@frameio/config';
import type {
  ResultOf,
  VariablesOf,
} from '@frameio/graphql-client/apollo/types';
import type {
  ColorThemeEnum,
  CreateShareFromAssets,
  CreateShareFromCollection,
  GetSharesForAddToShareMenuQueryVariables,
  Maybe,
} from '@frameio/graphql-client/generated';
import {
  IsGeneratedPassphrase,
  ProjectAppliedPolicies,
  useCreateShareFromAssetsMutation,
  useCreateShareFromCollectionMutation,
  useGetProjectAppliedPoliciesQuery,
} from '@frameio/graphql-client/generated';
import { parseFieldArguments } from '@frameio/graphql-client/utils/cache-utils/cache-utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import { trackingMarksStorage } from '@frameio/utils/storage/appUtilsStorage';
import { beginDeferredClipboardWrite } from '@frameio/vapor-migration/hooks/useCopyText/deferredClipboardWrite';
import { hasClipboardWritePermissions } from '@frameio/vapor-migration/hooks/useCopyText/useCopyText';
import { perf } from '@frameio/vapor-migration/utils/perf';
import { useActiveProjectId, useAppUiTheme } from '../../contexts';
import { useIsMatchingClientName } from '../../contexts/ClientNameContext';
import { useOpenLinkInBrowserFromUxp } from '../../integrations/hooks/useOpenLinkInBrowserFromUxp';
import { getLink } from '../../routing/getLink';
import { appComponentsStorage } from '../../storage';
import { useShareActionsContext } from '../contexts/ShareActionsContext';
import TEXT from './generated/TEXT/useCreateShare';
import {
  dispatchCopyShareLinkEvent,
  useCopyShareLink,
} from './useCopyShareLink';
import { useQuickshare } from './useQuickshare';

TEXT.DEFINE({
  /** Error message shown when Share creation fails */
  error: 'There was a problem creating your Share Link',
  /** Button label to copy the Share link */
  labelCopy: 'Copy Share Link',
  /** Button label to navigate to the created Share */
  labelGoToShare: 'Go to Share Link',
  /** Toast message when Share is created and user navigates to it */
  successWithRedirect: 'New Share Link created',
  /** Toast message when Share is created and link is copied */
  successWithoutRedirect: 'Share Link created and copied to clipboard',
  /** Message shown when user cancels Share creation */
  cancelledByUser: 'Cancelled by user',
  /** Error shown when restricted assets limit is exceeded */
  restrictedAssetsLimitReached:
    "Can't share items – this share would exceed the 250 restricted item limit.",
  /** Error shown when plan lacks disable_share_downloads feature */
  disableShareDownloadsNotInPlan:
    "Can't share items - your plan is missing feature: disable_share_downloads. Please contact support for assistance.",
  /** Error shown when plan lacks secure_sharing feature */
  secureSharingNotInPlan:
    "Can't share items - your plan is missing feature: secure_sharing. Please contact support for assistance.",
});
type CreateMutationData = {
  data:
    | ResultOf<typeof CreateShareFromAssets>
    | ResultOf<typeof CreateShareFromCollection>;
};

function getCreatedShare(data: CreateMutationData['data']) {
  if (!data) {
    return null;
  }

  if ('createShareFromAssets' in data && data.createShareFromAssets?.share) {
    return data.createShareFromAssets.share;
  }

  if (
    'createShareFromCollection' in data &&
    data.createShareFromCollection?.share
  ) {
    return data.createShareFromCollection.share;
  }

  return null;
}

function getMetadata(data: CreateMutationData['data']) {
  if (!data) {
    return null;
  }

  if ('createShareFromAssets' in data && data.createShareFromAssets?.metadata) {
    return data.createShareFromAssets.metadata;
  }

  if (
    'createShareFromCollection' in data &&
    data.createShareFromCollection?.metadata
  ) {
    return data.createShareFromCollection.metadata;
  }

  return null;
}

export type ShareToastOptions = {
  buttonLabel: string;
  buttonCallback: (args: CreateMutationData) => void;
  secondaryButtonLabel?: string;
  secondaryButtonCallback?: () => void;
  successMessage: string;
};

type CreateShareToastOptionsArgs = {
  copyLink: (
    data: CreateMutationData['data'],
    shouldDisplayToast?: boolean
  ) => void;
  shouldAutomaticallyRedirect: boolean;
  redirect: () => void;
};

/**
 * @description For Quick sharing without a redirect to the share builder we provide
 * primary and secondary button options: Copy Link and Go to Share, otherwise
 * we only provide a Copy Link button
 */
export async function getCreateShareToastOptions({
  copyLink,
  shouldAutomaticallyRedirect,
  redirect,
}: CreateShareToastOptionsArgs): Promise<ShareToastOptions> {
  const hasClipboardPermission = await hasClipboardWritePermissions();

  if (shouldAutomaticallyRedirect) {
    return {
      buttonLabel: TEXT.labelCopy,
      buttonCallback: ({ data }) => copyLink(data),
      successMessage: TEXT.successWithRedirect,
    };
  }

  if (hasClipboardPermission) {
    return {
      buttonLabel: TEXT.labelGoToShare,
      buttonCallback: () => {
        trackingMarksStorage.set(
          'share-builder-viewed-source',
          'AssetAddedSuccessToast',
          'session'
        );
        redirect();
      },
      successMessage: TEXT.successWithoutRedirect,
    };
  }

  return {
    buttonLabel: TEXT.labelCopy,
    buttonCallback: ({ data }) => copyLink(data, true),
    secondaryButtonLabel: TEXT.labelGoToShare,
    secondaryButtonCallback: redirect,
    successMessage: TEXT.successWithRedirect,
  };
}

export type CreateShareFromAssetsParams = VariablesOf<
  typeof CreateShareFromAssets
>;
export type CreateShareFromCollectionParams = VariablesOf<
  typeof CreateShareFromCollection
>;

export function useCreateShare() {
  const projectId = useActiveProjectId();
  const client = useApolloClient();

  useGetProjectAppliedPoliciesQuery({
    variables: { projectId: projectId! },
    skip: !projectId,
  });

  const tryCatchOnCreate = useToastTryCatchHandler({ duration: 4000 });
  const isWebClient = useIsMatchingClientName('web');
  const openLinkInBrowserFromUxp = useOpenLinkInBrowserFromUxp();

  const router = useRouter();

  const [createShareFromCollectionMutation] =
    useCreateShareFromCollectionMutation();

  const [createShareFromAssetsMutation] = useCreateShareFromAssetsMutation({
    onCompleted: (data) => {
      /**
       * @note store a reference that this share has been newly created in session storage.
       * This reference is later cleared by the `ShareBuilderAssetsPanel`. This state is
       * necessary for rendering certain UX, e.g., auto-focusing the share's title. This
       * is a less-than-ideal way of storing this state, compared to setting a client-
       * only field on the `Share` itself in the Apollo cache. However, those alternatives
       * are currently not reliable or under-performant. We might re-examine this strategy
       * in the future.
       */
      if (data.createShareFromAssets?.share) {
        appComponentsStorage.set(
          'frame-io--create-share',
          data.createShareFromAssets.share.id,
          'session'
        );
      }
    },
  });

  const copyShareLinkWithToast = useCopyShareLink();
  const copyShareLinkWithoutToast = useCopyShareLink({
    displaySuccessToast: false,
  });
  const { appUiTheme } = useAppUiTheme();
  const { checkCreateShareRestrictedAssets } = useShareActionsContext();
  const quickshare = useQuickshare();
  const createShareFn = useCallback(
    <
      F extends
        | typeof createShareFromAssetsMutation
        | typeof createShareFromCollectionMutation,
    >(
      shareFn: F
    ) => {
      return async (
        args:
          | VariablesOf<typeof CreateShareFromAssets>
          | VariablesOf<typeof CreateShareFromCollection>,
        options: { shouldRedirect?: boolean; shouldDisplayToast?: boolean } = {
          shouldRedirect: true,
          shouldDisplayToast: true,
        }
      ): Promise<Maybe<ReturnType<F>>> => {
        // eslint-disable-next-line no-param-reassign
        options = {
          shouldRedirect: true,
          shouldDisplayToast: !quickshare.isEnabled(),
          ...options,
        };

        if (quickshare.isEnabled()) {
          // eslint-disable-next-line no-param-reassign
          options.shouldDisplayToast = false;
        }

        /**
         * When creating a new Share, we want to set its starting color theme based on the User's app settings, with
         * the app's UI theme setting by default.
         */
        let colorTheme = appUiTheme?.toUpperCase() as
          | ColorThemeEnum
          | undefined;

        // If the app's ui theme is set to auto, then set the color theme based off the system's preferred color sceheme
        if (appUiTheme === 'auto') {
          colorTheme =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: light)').matches
              ? 'LIGHT'
              : 'DARK';
        }

        const isQuickshareEnabled = quickshare.isEnabled();
        const deferredClipboardWrite = isQuickshareEnabled
          ? beginDeferredClipboardWrite()
          : null;

        try {
          // if we are running a `createShareFromAssetsMutation` perform a
          // check and present a confirmation dialog if the selection contains
          // restricted assets
          const assetIds = 'assetIds' in args ? args.assetIds : undefined;
          const { confirmed, hasRestrictedAssets } =
            await checkCreateShareRestrictedAssets(assetIds);

          if (!confirmed) {
            return null;
          }

          const shareProjectId =
            ('projectId' in args && args.projectId) || projectId;

          if (!shareProjectId) {
            return null;
          }

          const variables: typeof args = {
            ...args,
            colorTheme,
            generatePassphrase: shouldGeneratePassphrase(
              client,
              shareProjectId
            ),
            shareRestrictedAssets: hasRestrictedAssets,
          };

          const redirect = () => {
            const successResult = getCreatedShare(response.data);

            if (successResult?.id) {
              trackingMarksStorage.set(
                'share-builder-viewed-source',
                'AutomaticRedirectAfterCreation',
                'session'
              );
              const href = getLink({
                page: 'shareBuilder',
                projectId: shareProjectId,
                shareId: successResult?.id,
              });

              if (isWebClient) {
                router.push(href);
              } else {
                openLinkInBrowserFromUxp(href);
              }
            }
          };

          const shouldAutomaticallyRedirect = options?.shouldRedirect;
          const copyLink = async (
            data: CreateMutationData['data'],
            shouldDisplayToast?: boolean
          ) => {
            const host = CONFIG.appServerOrigin;
            const result = getCreatedShare(data);

            if (!result) {
              return;
            }

            const url = result?.url ?? `${host}/share/${result?.id}`;

            if (shouldDisplayToast) {
              await copyShareLinkWithToast({
                actionSource: 'CreationToast', // When using Copy Link button on a toast
                shareId: result?.id,
                shareUrl: url,
              });
            } else {
              await copyShareLinkWithoutToast({
                actionSource: 'QuickShare', // When using Create Share Link right-click menu item
                shareId: result?.id,
                shareUrl: url,
                skipAnalytics: true, // We don't want to track link creation for QuickShare
              });
            }
          };

          const shareToastOptions = await getCreateShareToastOptions({
            copyLink,
            shouldAutomaticallyRedirect: Boolean(shouldAutomaticallyRedirect),
            redirect,
          });
          const response = await tryCatchOnCreate({
            attempt: async () => {
              perf('create-share:start');
              const result = await shareFn({
                variables: variables as any,
                refetchQueries: ['GetSharesForAddToShareMenu'],
                update: (cache, { data }) => {
                  if (data) {
                    const newShare = getCreatedShare(data);
                    const metadata = getMetadata(data);

                    /**
                     * If the share is created with an auto-generated passphrase, update
                     * the new share's client-side field.
                     */
                    if (metadata?.passphraseGenerated) {
                      cache.writeFragment({
                        fragment: IsGeneratedPassphrase,
                        id: cache.identify({
                          __typename: 'Share',
                          id: newShare?.id,
                        }),
                        data: { isGeneratedPassphrase: true },
                      });
                    }

                    cache.modify({
                      id: cache.identify({
                        __typename: 'Project',
                        id: shareProjectId,
                      }),
                      fields: {
                        shares: (
                          value,
                          { toReference, storeFieldName, fieldName }
                        ) => {
                          const existingNodes = value.nodes ?? [];

                          /**
                           * @note (V4BG-1915): Cache manipulation is a grind, Vol. 2.14e8
                           * Don't update cache entries of `Project.shares` that have been queried with
                           * filters for ad-hoc shares if the new share is a `CollectionShare`.
                           */
                          if (newShare?.__typename === 'CollectionShare') {
                            const fieldArgs =
                              parseFieldArguments<GetSharesForAddToShareMenuQueryVariables>(
                                storeFieldName,
                                fieldName
                              );

                            if (
                              fieldArgs?.filters?.aggregationMode ===
                                'STATIC' ||
                              fieldArgs?.filters?.origin === 'AD_HOC_ASSETS'
                            ) {
                              return value;
                            }
                          }

                          return {
                            ...value,
                            nodes: newShare
                              ? [toReference(newShare), ...existingNodes]
                              : existingNodes,
                            totalCount: value.totalCount + 1,
                          };
                        },
                      },
                    });
                  }
                },
              });
              perf('create-share:end');
              return result;
            },
            ...(options?.shouldDisplayToast ? shareToastOptions : {}),
            errorMessage(error: ApolloError) {
              if (
                error?.graphQLErrors[0]?.extensions?.code ===
                'FAILED_PRECONDITION'
              ) {
                const message = error?.graphQLErrors[0]?.message;

                switch (true) {
                  case isDisableShareDownloadsNotInPlan(message):
                    return TEXT.disableShareDownloadsNotInPlan;
                  case isSecureSharingNotInPlan(message):
                    return TEXT.secureSharingNotInPlan;
                  case isTooManyRestrictedItemsInShare(message):
                    return TEXT.restrictedAssetsLimitReached;
                  default:
                    return TEXT.error;
                }
              }

              return TEXT.error;
            },
          });

          const isEmptyShare =
            (args as CreateShareFromAssetsParams).assetIds &&
            (args as CreateShareFromAssetsParams).assetIds.length === 0;

          if (!isEmptyShare) {
            const createdShare = getCreatedShare(response.data);
            const createdShareId = createdShare?.id ?? null;
            let didCopyLink = false;

            if (createdShareId) {
              const host = CONFIG.appServerOrigin;
              const shareUrl =
                createdShare?.url ?? `${host}/share/${createdShareId}`;
              const openedQuickshare = Boolean(quickshare(createdShareId));

              if (deferredClipboardWrite) {
                didCopyLink = await deferredClipboardWrite.complete(shareUrl);
                if (didCopyLink && openedQuickshare) {
                  appComponentsStorage.set(
                    'frame-io--quickshare-link-copied',
                    createdShareId,
                    'session'
                  );
                  dispatchCopyShareLinkEvent(shareUrl);
                }
              } else if (!isQuickshareEnabled) {
                try {
                  await copyLink(response.data, false);
                  didCopyLink = true;
                } catch {
                  // Clipboard write failed; quickshare still opens without auto-copy UI.
                }
              }
            }
          } else if (shouldAutomaticallyRedirect) {
            copyLink(response.data, !options?.shouldDisplayToast);
            redirect();
          } else {
            copyLink(response.data, !options?.shouldDisplayToast);
          }

          return response;
        } finally {
          deferredClipboardWrite?.cancel();
        }
      };
    },
    [
      appUiTheme,
      client,
      tryCatchOnCreate,
      projectId,
      router,
      copyShareLinkWithToast,
      copyShareLinkWithoutToast,
      checkCreateShareRestrictedAssets,
      quickshare,
      isWebClient,
      openLinkInBrowserFromUxp,
    ]
  );

  return {
    createShareFromAssets: createShareFn(createShareFromAssetsMutation),
    createShareFromCollection: createShareFn(createShareFromCollectionMutation),
  };
}

function isDisableShareDownloadsNotInPlan(message: string): boolean {
  return /not included in plan: disable_share_downloads/.test(message);
}

function isSecureSharingNotInPlan(message: string): boolean {
  return /not included in plan: secure_sharing/.test(message);
}

function isTooManyRestrictedItemsInShare(message: string): boolean {
  return /too many restricted assets in the request/.test(message);
}

/**
 * Utility function that returns whether new shares should generate a passphrase based on the
 * `shareAccess.default` and `sharePassphrase.required` applied policies
 * @param client {ApolloClient} The Apollo client object
 * @param projectId {string} The projectId to read applied resource control policies off of
 * @returns
 */
function shouldGeneratePassphrase(
  client: ApolloClient<any>,
  projectId: string
) {
  const appliedResourceControlPolicies = client.readFragment({
    fragment: ProjectAppliedPolicies,
    fragmentName: 'ProjectAppliedPolicies',
    id: client.cache.identify({ __typename: 'Project', id: projectId }),
  })?.appliedResourceControlPolicies;

  return (
    appliedResourceControlPolicies?.shareAccess?.default === 'PUBLIC' &&
    appliedResourceControlPolicies?.sharePassphrase?.required
  );
}
